import {useContext, useEffect} from 'react'
import React from 'react'
import {curry, pipe} from 'ramda'
import {ProviderRedirectQueryParamsType} from 'types/ProviderRedirect'

import {NoStyleLink} from '@vio/atlas/helpers/NoStyleLink'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {augmentBookUriWithAnonymousId} from '@vio/core/src/offer/business/offers'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

import {AlternativeOffersContext} from './AlternativeOffersContext'

type AlternativeOfferRedirectParams = Pick<
  ProviderRedirectQueryParamsType,
  'redirectURI' | 'lang' | 'currency' | 'providerCode'
>

const composeProviderRedirectURL = (
  searchHost: string,
  redirectParams: AlternativeOfferRedirectParams
) => {
  const {redirectURI, lang, currency, providerCode} = redirectParams

  const queryStringParams = new URLSearchParams({
    redirectURI: btoa(redirectURI),
    lang,
    currency,
    providerCode,
    // enforce language change, because user might have changed the language on checkout
    forceLanguageChange: '1'
  }).toString()

  return `${searchHost}/ProviderRedirect?${queryStringParams}`
}

type RedirectUriParams = {
  anonymousId?: string
}

const augumentRedirectURIWithParams = (
  redirectUri: string,
  params: RedirectUriParams
) => {
  return pipe<[string], string>(
    curry(augmentBookUriWithAnonymousId)(params.anonymousId)
  )(redirectUri)
}

type Props = {
  offer: SearchOffer
  children: React.ReactNode
}

export const AlternativeOfferInteraction: React.FC<Props> = ({
  offer,
  children
}: Props) => {
  const {onOfferClick, onOfferViewed, lang, anonymousId} = useContext(
    AlternativeOffersContext
  )
  const {brandSiteAddress} = useBrand()
  const {currency, url} = offer
  const {ref, inView} = useInView({triggerOnce: true})
  const isEmbedded = useIsEmbeddedWithBrand()

  const redirectParams: AlternativeOfferRedirectParams = {
    currency,
    providerCode: offer.intermediaryProvider,
    redirectURI: augumentRedirectURIWithParams(url, {
      anonymousId
    }),
    lang
  }

  useEffect(() => {
    if (inView) {
      onOfferViewed?.(offer)
    }
  }, [inView, offer, onOfferViewed])

  return (
    <NoStyleLink
      ref={ref}
      href={composeProviderRedirectURL(brandSiteAddress, redirectParams)}
      onClick={() => onOfferClick?.(offer)}
      target={isEmbedded ? '_self' : '_blank'}
      rel="noopener noreferrer"
    >
      {children}
    </NoStyleLink>
  )
}
